import React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../../utils';
import { withTheme } from 'styled-components';
import { styled } from '../../../utils/styled';
import { propStyles } from '../../../utils/propStyles';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { DataStore, SuperscriptScope } from '../../../stores';
import { translate } from '../../Translate';
import { Theme } from '../../../models/Theme';
import { DropdownItem } from '../../../models/components/Dropdown/DropdownItem';
import InlineDisclaimers from '../../InlineDisclaimers';
import {
    ChildReferenceComponent,
    ChildReferenceProps,
} from '../../../utils/accessibility/ChildReferenceComponent';

const StyledLi = styled<any, any>('li', 'styledLi', 'dropdownListItem')`
    ${propStyles};
`;

StyledLi.defaultProps = {
    fontFamily: 'body',
    fontWeight: 'normal',
    paddingY: 'sm',
    paddingX: 'md',
    borderWidth: '1px',
    borderStyle: 'none none solid none',
    borderColor: 'gray300',
    cursor: 'pointer',
    alternated: {
        bgColor: 'gray100',
    },
    hover: {
        bgColor: 'gray100',
    },
    selected: {
        color: 'primary',
        fontWeight: 'bold',
    },
};

interface Props<THtmlInstance> extends ChildReferenceProps<THtmlInstance> {
    index: number;
    item: DropdownItem;
    isOpen: boolean;
    isFocused: boolean;
    dataAlternated: boolean;
    dataSelected: boolean;
    dataMetricId: string;
    itemLabelFn?: (item: DropdownItem) => string;
    dataStore?: DataStore;
    theme?: Theme;
    superscriptScope?: SuperscriptScope;
}

@inject('dataStore')
@observer
class DropdownListItem extends ChildReferenceComponent<
    Props<HTMLLIElement>,
    HTMLLIElement
> {
    get shouldApplyFocus() {
        return this.props.isOpen && this.props.isFocused;
    }

    get renderProps() {
        const { dataAlternated, dataMetricId, dataSelected } = this.props;
        return {
            role: 'option',
            tabIndex: 0,
            'aria-selected': dataSelected,
            'data-alternated': dataAlternated,
            'data-selected': dataSelected,
            'data-metric-id': dataMetricId,
            ...this.interactiveProps,
        };
    }

    getItemLabel(item: DropdownItem): string {
        if (item) {
            const { itemLabelFn } = this.props;
            if (itemLabelFn) {
                return itemLabelFn(item);
            }
            return item.label;
        }
        return;
    }

    render() {
        const {
            theme,
            item,
            dataStore,
            superscriptScope,
            ...props
        } = this.props;
        const itemLabel = this.getItemLabel(item);
        const translatedLabel =
            (translate && translate(dataStore.stores.translation, itemLabel)) ||
            itemLabel;

        return (
            <StyledLi
                {...this.renderProps}
                {...theme.DropdownListItem}
                {...props}
            >
                {translatedLabel}
                <InlineDisclaimers
                    conditionalDisclaimers={item.disclaimers}
                    accessibilityContext={`for disclaimers of the dropdown item ${translatedLabel}`}
                    forceSuperScripts={dataStore.isMobile}
                    superscriptScope={superscriptScope}
                    analyticsContext={{
                        metricContext: `dropDown-item-${camelCase(item.label)}`,
                        source: 'dropdownListItem',
                        sourceContext: translatedLabel,
                    }}
                />
            </StyledLi>
        );
    }
}

export default withTheme(DropdownListItem);



// WEBPACK FOOTER //
// ./src/Components/Dropdown/Components/DropdownListItem.tsx